import * as React from "react";
import {
  AppBar,
  BottomNavigation,
  BottomNavigationAction,
  Box,
  IconButton,
  Paper,
  Toolbar,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ListIcon from "@mui/icons-material/List";
import BarChartIcon from "@mui/icons-material/BarChart";
import DescriptionIcon from "@mui/icons-material/Description";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";

import { MobileActionBar, MobileActionBarMode } from "./MobileActionBar";
import { MobileErrorBanner } from "./MobileErrorBanner";
import { MobileKpiView } from "./MobileKpiView";
import { MobilePaTypeView } from "./MobilePaTypeView";
import { MobileSectionPicker, MobileSectionPickerItem } from "./MobileSectionPicker";
import { MobileSectionView } from "./MobileSectionView";
import { MobileNavConfirm } from "./MobileNavConfirm";
import { PAType, PAMode } from "../../ProfitAnalysisPage";
import { RunningTotalsProps } from "../../../../types";

export type MobileTab = "kpi" | "details" | "patype";

export interface MobileShellProps {
  paType: PAType;
  mode: PAMode;
  isReviewing: boolean;
  hasUnsavedChanges: boolean;

  // Section picker
  sectionItems: MobileSectionPickerItem[];
  /** Given a section id, return the JSX for that section.  Owned by parent. */
  renderSection: (sectionId: string) => React.ReactNode;
  /** Title shown in the section view header (parent-owned label map). */
  sectionTitle: (sectionId: string) => string;
  /** Trailing summary shown in the section view header (e.g. dollar total). */
  sectionTrailing?: (sectionId: string) => string | undefined;

  // KPI data
  runningTotals: RunningTotalsProps;
  formatCurrency: (value: number) => string;
  exitStrategy: number;
  totalCost: number;
  varianceNAGuard?: boolean;

  // Errors
  errorCount: number;
  /** Section id to jump to (first section containing an error). */
  firstErrorSectionId?: string;

  // Action handlers
  primaryDisabled: boolean;
  printDisabled?: boolean;
  onPrint: () => void;
  onReview: () => void;
  onExitReview: () => void;
  onSubmit: () => void;
  onSaveChanges: () => void;

  // Navigation
  onNavigateToList: () => void;
}

/**
 * The full mobile shell — app bar, view router, error banner, action bar,
 * and bottom nav.  Lives below the `md` breakpoint.  Desktop layout is
 * rendered by ProfitAnalysisPage unchanged.
 *
 * View routing:
 *   bottom-nav tab "kpi"     -> MobileKpiView
 *   bottom-nav tab "details" -> MobileSectionPicker OR MobileSectionView
 *   bottom-nav tab "patype"  -> MobilePaTypeView
 *
 * The action bar and error banner are only shown on the Details tab.  On KPI
 * and PA Type tabs the user is browsing read-only summaries; primary actions
 * belong with the form they affect.
 */
export function MobileShell({
  paType,
  mode,
  isReviewing,
  hasUnsavedChanges,
  sectionItems,
  renderSection,
  sectionTitle,
  sectionTrailing,
  runningTotals,
  formatCurrency,
  exitStrategy,
  totalCost,
  varianceNAGuard,
  errorCount,
  firstErrorSectionId,
  primaryDisabled,
  printDisabled,
  onPrint,
  onReview,
  onExitReview,
  onSubmit,
  onSaveChanges,
  onNavigateToList,
}: MobileShellProps) {
  const [tab, setTab] = React.useState<MobileTab>("details");
  const [selectedSectionId, setSelectedSectionId] = React.useState<string | null>(null);

  // Unsaved-changes guard for navigation away from the page
  const [pendingNav, setPendingNav] = React.useState<null | "list">(null);

  const attemptNavigateToList = () => {
    if (mode === "view" || !hasUnsavedChanges) {
      onNavigateToList();
      return;
    }
    setPendingNav("list");
  };

  const handleBack = () => {
    // Inside a section -> back to section list
    if (tab === "details" && selectedSectionId) {
      setSelectedSectionId(null);
      return;
    }
    // Otherwise -> navigate to PA list (with unsaved guard)
    attemptNavigateToList();
  };

  const handleErrorBannerJump = (sectionId: string) => {
    setTab("details");
    setSelectedSectionId(sectionId);
  };

  // Show the action bar + error banner only on the Details tab — primary
  // actions belong with the form, not with the KPI/PA-type summary tabs.
  const showActionFooter = tab === "details";

  const isViewMode = mode === "view";
  const headerSubtitle = paType === "spec" ? "Spec Job" : "Sold Job";

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        backgroundColor: "background.default",
      }}
    >
      {/* App bar */}
      <AppBar position="sticky" elevation={1} sx={{ backgroundColor: "#0a2540" }}>
        <Toolbar variant="dense" sx={{ minHeight: 48, px: 1 }}>
          <IconButton edge="start" color="inherit" onClick={handleBack} aria-label="back">
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ flex: 1, overflow: "hidden" }}>
            <Typography variant="subtitle1" noWrap sx={{ fontWeight: 500, lineHeight: 1.2 }}>
              {tab === "details" && selectedSectionId
                ? sectionTitle(selectedSectionId)
                : "Profit Analysis"}
            </Typography>
            {!(tab === "details" && selectedSectionId) && (
              <Typography variant="caption" sx={{ opacity: 0.75, lineHeight: 1 }}>
                {headerSubtitle}
                {isReviewing && " · Reviewing"}
              </Typography>
            )}
          </Box>
          <IconButton
            color="inherit"
            onClick={attemptNavigateToList}
            aria-label="profit analysis list"
          >
            <ListIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* View body */}
      <Box
        component="main"
        sx={{
          flex: 1,
          // Reserve room for the fixed footer stack (error banner + action bar
          // + bottom nav).  Worst case ~ 170px; safe to over-reserve a bit.
          pb: showActionFooter ? (errorCount > 0 ? 22 : 16) : 8,
        }}
      >
        {tab === "kpi" && (
          <MobileKpiView
            runningTotals={runningTotals}
            formatCurrency={formatCurrency}
            exitStrategy={exitStrategy}
            totalCost={totalCost}
            profitUSD={runningTotals.profitUSD}
            profitMargin={runningTotals.profitMargin}
            varianceToExitStrategy={runningTotals.varianceToExitStrategy}
            varianceNAGuard={varianceNAGuard}
            paType={paType}
            bpPrice={runningTotals.bpPrice}
            bpProfit={runningTotals.bpProfit}
            bpPercent={runningTotals.bpPercent}
            blPrice={runningTotals.blPrice}
            blProfit={runningTotals.blProfit}
            blPercent={runningTotals.blPercent}
            exitPrice={runningTotals.exitPrice}
            exitProfit={runningTotals.exitProfit}
            exitPercent={runningTotals.exitPercent}
          />
        )}

        {tab === "details" && !selectedSectionId && (
          <MobileSectionPicker
            items={sectionItems}
            readOnly={isReviewing}
            onSelect={(id) => setSelectedSectionId(id)}
          />
        )}

        {tab === "details" && selectedSectionId && (
          <MobileSectionView
            title={sectionTitle(selectedSectionId)}
            trailing={sectionTrailing?.(selectedSectionId)}
            onBack={() => setSelectedSectionId(null)}
          >
            {renderSection(selectedSectionId)}
          </MobileSectionView>
        )}

        {tab === "patype" && <MobilePaTypeView paType={paType} mode={mode} />}
      </Box>

      {/* Fixed footer stack */}
      <Paper
        elevation={3}
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: (t) => t.zIndex.appBar,
        }}
      >
        {showActionFooter && (
          <>
            <MobileErrorBanner
              errorCount={errorCount}
              jumpToSectionId={firstErrorSectionId}
              onJump={handleErrorBannerJump}
            />
            <MobileActionBar
              mode={mode as MobileActionBarMode}
              isReviewing={isReviewing}
              primaryDisabled={primaryDisabled}
              showPrint={true}
              printDisabled={printDisabled}
              onPrint={onPrint}
              onReview={onReview}
              onExitReview={onExitReview}
              onSubmit={onSubmit}
              onSaveChanges={onSaveChanges}
            />
          </>
        )}

        <BottomNavigation
          showLabels
          value={tab}
          onChange={(_e, newValue: MobileTab) => {
            setTab(newValue);
            // Switching tabs clears any selected section
            if (newValue !== "details") setSelectedSectionId(null);
          }}
        >
          <BottomNavigationAction
            value="kpi"
            label="KPI"
            icon={<BarChartIcon />}
          />
          <BottomNavigationAction
            value="details"
            label="Details"
            icon={<DescriptionIcon />}
          />
          <BottomNavigationAction
            value="patype"
            label="PA Type"
            icon={<LocalOfferIcon />}
          />
        </BottomNavigation>
      </Paper>

      <MobileNavConfirm
        open={pendingNav !== null}
        onCancel={() => setPendingNav(null)}
        onConfirm={() => {
          setPendingNav(null);
          onNavigateToList();
        }}
      />
    </Box>
  );
}
